import React, { useEffect, useState } from "react";
import GridComponent from "../../../components/GridComponent";
import {
  columns,
  filterOptions,
} from "../../../utils/Customer/CustomerData";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Filter,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import Dropdown from "../../../components/customer/Dropdown";
import TextInputComp from "../../../components/customer/TextInputComp";
import {
  useLazyGetAdminProductsByIdQuery,
  useLazyGetAdminProductsQuery,
  useUpdateProductStatusMutation,
} from "../../../redux/actions/Admin/ProductAdmin";
import CommonButton from "../../../components/CommonButton";
import { INVENTORY_EXPORT } from "../../../config/apiUrlsAdmin";

// ============================================================
// DROPDOWN OPTION TYPE
// ============================================================
export type Option = {
  id: string;
  label: string;
  value: string;
  icon?: React.ReactNode;
  image?: string;
};

// ============================================================
// NOTIFICATION TYPE
// ============================================================
type NotificationType = "success" | "error";

interface NotificationState {
  type: NotificationType;
  message: string;
}

// ============================================================
// COMPONENT
// ============================================================
const AdminProductManagement: React.FC = () => {
  const navigate = useNavigate();

  // ============================================================
  // MANAGEMENT STATE
  // ============================================================
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedFilter, setSelectedFilter] =
    useState<Option | null>(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isExporting, setIsExporting] = useState(false);

  // ============================================================
  // NOTIFICATION STATE
  // ============================================================
  const [notification, setNotification] =
    useState<NotificationState | null>(null);

  // ============================================================
  // API HOOKS
  // ============================================================
  const [
    getAdminProducts,
    { data, isLoading, isError },
  ] = useLazyGetAdminProductsQuery();

  const [updateProductStatus] =
    useUpdateProductStatusMutation();
      const [
        fetchProductData,
        
        
      ] = useLazyGetAdminProductsByIdQuery();

  // ============================================================
  // AUTO HIDE NOTIFICATION
  // ============================================================
  useEffect(() => {
    if (!notification) return;

    const timer = setTimeout(() => {
      setNotification(null);
    }, 3000);

    return () => clearTimeout(timer);
  }, [notification]);

  // ============================================================
  // SEARCH DEBOUNCE
  // ============================================================
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPageNumber(1);
    }, 500);

    return () => clearTimeout(delayTimer);
  }, [searchText]);

  // ============================================================
  // FETCH PRODUCTS
  // ============================================================
  useEffect(() => {
    const requestParams: any = {
      pageNumber,
      pageSize,
      searchText: debouncedSearch.trim() || undefined,
    };

    // ==========================================================
    // FILTER
    // ==========================================================
    if (selectedFilter?.value) {
      if (selectedFilter.value === "active") {
        requestParams.isActive = true;
      }

      if (selectedFilter.value === "inactive") {
        requestParams.isActive = false;
      }

      if (selectedFilter.value === "published") {
        requestParams.isPublished = true;
      }

      if (selectedFilter.value === "draft") {
        requestParams.isPublished = false;
      }
    }

    getAdminProducts(requestParams);
  }, [
    getAdminProducts,
    pageNumber,
    pageSize,
    debouncedSearch,
    selectedFilter,
  ]);

  // ============================================================
  // MAP API RESPONSE TO GRID
  // ============================================================
  const gridProducts =
    data?.products?.map((item) => ({
      id: item.productId,
      productname: item.productName,
      sku: item.sku ?? "-",
      quantity: item.stock,
      price: item.price,
      status: item.isActive,
      productimage: item.thumbnail,
      brandname: item.brandName,
      productcode: item.productCode,
      barcode: item.barcode ?? "-",
      color: item.color ?? "-",
      size: item.size ?? "-",
      weight: item.weight ?? "-",
      hasFamily: item.hasFamily,
      imageCount: item.imageCount,
    })) ?? [];

  // ============================================================
  // PAGINATION
  // ============================================================
  const totalRecords = data?.totalRecords || 0;

  const totalPages =
    Math.ceil(totalRecords / pageSize) || 1;

  // ============================================================
  // EDIT PRODUCT
  // ============================================================
  const handleEditClick = (
    row: Record<string, any>
  ) => {
    navigate(
      `/admin/product/productmanagement/producteditscreen/${row.id}`
    );
  };


const handleDuplicateClick = async (row: Record<string, any>) => {
  try {
    // 1. Fetch complete product details by ID
    const fullProductData = await fetchProductData(row.id).unwrap();

    if (!fullProductData) return;

    // 2. Clone and strip out ID, SKU, Barcode, and Product Code as requested
    const duplicatedPayload = {
      ...fullProductData,
      productId: 0,
      productCode: "",
      slug: "", // Let it regenerate or clear
      variant: fullProductData.variant ? {
        ...fullProductData.variant,
        variantId: 0,
        sku: "",
        barcode: "",
      } : undefined,
      images: (fullProductData.images || []).map((img: any) => ({
        ...img,
        imageId: 0,
      })),
      attributes: (fullProductData.attributes || []).map((attr: any) => ({
        ...attr,
      })),
    };

    // 3. Store into localStorage so the Add Screen can pick it up
    localStorage.setItem("duplicated_product_payload", JSON.stringify(duplicatedPayload));

    // 4. Navigate to Add Product screen
    navigate("/admin/product/addproduct"); // Adjust route to your add page path
  } catch (error) {
    console.error("Failed to load product data for duplication:", error);
  }
};
  // ============================================================
  // STATUS TOGGLE
  // ============================================================
  const handleStatusToggle = async (
    clickedRow: any
  ) => {
    const nextStatus = !clickedRow.status;

    try {
      const response =
        await updateProductStatus({
          id: clickedRow.id,
          data: {
            isActive: nextStatus,
          },
        }).unwrap();

      // ======================================================
      // SUCCESS NOTIFICATION
      // ======================================================
      setNotification({
        type: "success",
        message:
          response?.message ||
          `Product ${
            nextStatus
              ? "activated"
              : "deactivated"
          } successfully.`,
      });

      // ======================================================
      // REFRESH CURRENT LIST
      // ======================================================
      const requestParams: any = {
        pageNumber,
        pageSize,
        searchText:
          debouncedSearch.trim() || undefined,
      };

      if (selectedFilter?.value === "active") {
        requestParams.isActive = true;
      }

      if (selectedFilter?.value === "inactive") {
        requestParams.isActive = false;
      }

      if (selectedFilter?.value === "published") {
        requestParams.isPublished = true;
      }

      if (selectedFilter?.value === "draft") {
        requestParams.isPublished = false;
      }

      getAdminProducts(requestParams);
    } catch (error: any) {
      // ======================================================
      // ERROR NOTIFICATION
      // ======================================================
      setNotification({
        type: "error",
        message:
          error?.data?.message ||
          error?.data?.Message ||
          "Failed to update product status.",
      });
    }
  };

  // ============================================================
  // EXPORT INVENTORY
  // ============================================================
  const handleDownloadMasterInventory =
    async () => {
      try {
        setIsExporting(true);

        const response = await fetch(
          INVENTORY_EXPORT,
          {
            method: "GET",
              credentials: "include",
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "token"
              )}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            "Failed file download operation."
          );
        }

        const blob = await response.blob();

        const url =
          window.URL.createObjectURL(blob);

        const link =
          document.createElement("a");

        link.href = url;

        link.download = `Petland_Inventory_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;

        document.body.appendChild(link);

        link.click();

        link.remove();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(
          "Export download dropped execution context maps:",
          error
        );
      } finally {
        setIsExporting(false);
      }
    };

  // ============================================================
  // FILTER OPTIONS
  // ============================================================
  const updatedDropdownFilterOptions: Option[] =
    [
      {
        id: "all",
        label: "All Items",
        value: "",
      },
      {
        id: "active",
        label: "Active Status Only",
        value: "active",
      },
      {
        id: "inactive",
        label: "Inactive Only",
        value: "inactive",
      },
    ];

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="relative p-3 text-xs text-slate-700">
      {/* ========================================================
          TOP RIGHT NOTIFICATION
      ========================================================= */}
      {notification && (
        <div
          className={`
            fixed
            top-4
            right-4
            z-[9999]

            w-[calc(100%-2rem)]
            sm:w-auto
            sm:min-w-[280px]
            sm:max-w-[420px]

            px-4
            py-3

            rounded-xl
            shadow-lg

            border

            text-xs
            font-bold

            flex
            items-start
            gap-2

            transition-all
            duration-300

            ${
              notification.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-800"
            }
          `}
        >
          <AlertCircle
            size={15}
            className="shrink-0 mt-0.5"
          />

          <span className="break-words leading-5">
            {notification.message}
          </span>
        </div>
      )}

      {/* ========================================================
          MANAGEMENT CONTROLS
      ========================================================= */}
      <div
        className="
          flex
          flex-col
          lg:flex-row
          lg:justify-between
          lg:items-center

          mb-3

          gap-3
        "
      >
        <h1
          className="
            text-lg
            md:text-2xl
            font-bold
            text-logoHeading
            uppercase
            tracking-tight
          "
        >
          Product Management
        </h1>

        <div
          className="
            flex
            flex-col
            md:flex-row
            md:items-center
            md:gap-5

            gap-3
          "
        >
          {/* ====================================================
              SEARCH
          ===================================================== */}
          <div
            className="
              relative
              w-full
              sm:w-[250px]
            "
          >
            <Search
              size={18}
              className="
                absolute
                left-3
                top-1/2
                -translate-y-1/2
                text-gray-400
                z-10
              "
            />

            <TextInputComp
              placeholder="Search Product..."
              value={searchText}
              onChangeText={(text) =>
                setSearchText(text)
              }
              className="pl-10"
            />
          </div>

          {/* ====================================================
              FILTER
          ===================================================== */}
          <div className="w-full md:w-[150px]">
            <Dropdown
              options={
                updatedDropdownFilterOptions
              }
              value={selectedFilter}
              onChange={(option: any) => {
                setSelectedFilter(
                  option as Option
                );

                setPageNumber(1);
              }}
              dropdownIcon={
                <Filter
                  size={18}
                  className="text-logoHeading"
                />
              }
            />
          </div>
        </div>
      </div>

      {/* ========================================================
          EXPORT BUTTON
      ========================================================= */}
      <div className="flex justify-end mb-3">
        <CommonButton
          text={
            isExporting
              ? "Exporting..."
              : "Export Current Stock"
          }
          variant="outline"
          icon={
            isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )
          }
          disabled={
            isExporting || isLoading
          }
          className="
            border-slate-200
            text-slate-700
            font-semibold
            text-xs
          "
          onClick={
            handleDownloadMasterInventory
          }
        />
      </div>

      {/* ========================================================
          DATA
      ========================================================= */}
      {isError ? (
        <div
          className="
            p-6
            text-center
            text-rose-500
            font-bold
            border
            border-rose-100
            bg-rose-50
            rounded-xl
          "
        >
          Could not retrieve active product
          index structures catalog maps.
        </div>
      ) : isLoading ? (
        <div
          className="
            p-12
            text-center
            text-slate-400
            italic
            font-medium
            flex
            items-center
            justify-center
            gap-1.5
          "
        >
          <Loader2
            size={14}
            className="animate-spin text-[#2F5E2E]"
          />

          Loading active listings entries data
          metrics...
        </div>
      ) : (
        <div className="space-y-4">
          {/* ======================================================
              GRID
          ======================================================= */}
          <GridComponent
            columns={columns}
            data={gridProducts}
            pageSize={pageSize}
            handleEditClick={handleEditClick}
            handleStatusToggle={
              handleStatusToggle
            }
            handleDuplicateClick={handleDuplicateClick}
          />

          {/* ======================================================
              PAGINATION
          ======================================================= */}
          <div
            className="
              bg-white
              p-3
              rounded-xl
              border
              border-slate-200

              flex
              flex-col
              sm:flex-row

              justify-between
              items-center

              gap-4

              select-none

              shadow-3xs
            "
          >
            {/* ==================================================
                RECORD COUNT
            =================================================== */}
            <div className="text-slate-500 font-medium text-center sm:text-left">
              Showing{" "}
              <span className="font-bold text-slate-800">
                {gridProducts.length}
              </span>{" "}
              items out of{" "}
              <span className="font-bold text-slate-800">
                {totalRecords}
              </span>{" "}
              total logged files.
            </div>

            {/* ==================================================
                PAGINATION CONTROLS
            =================================================== */}
            <div
              className="
                flex
                flex-col
                sm:flex-row

                items-center

                gap-3
              "
            >
              {/* =================================================
                  ROWS PER PAGE
              ================================================== */}
              <div
                className="
                  flex
                  items-center
                  gap-1.5
                "
              >
                <span className="text-slate-400 font-semibold">
                  Rows per page:
                </span>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(
                      Number(e.target.value)
                    );

                    setPageNumber(1);
                  }}
                  className="
                    h-7

                    border
                    border-slate-200

                    rounded-lg

                    bg-slate-50

                    px-1

                    font-bold
                    text-slate-700

                    focus:outline-none

                    cursor-pointer
                  "
                >
                  <option value={5}>
                    5
                  </option>

                  <option value={10}>
                    10
                  </option>

                  <option value={20}>
                    20
                  </option>

                  <option value={50}>
                    50
                  </option>
                </select>
              </div>

              {/* =================================================
                  PAGE NAVIGATION
              ================================================== */}
              <div
                className="
                  flex
                  items-center
                  gap-1
                "
              >
                <button
                  type="button"
                  disabled={pageNumber === 1}
                  onClick={() =>
                    setPageNumber(
                      (prev) =>
                        Math.max(
                          prev - 1,
                          1
                        )
                    )
                  }
                  className="
                    w-7
                    h-7

                    flex
                    items-center
                    justify-center

                    border
                    rounded-lg

                    bg-white
                    hover:bg-slate-50

                    disabled:bg-slate-100

                    text-slate-600
                    disabled:text-slate-300

                    transition
                  "
                >
                  <ChevronLeft size={14} />
                </button>

                <div
                  className="
                    text-[11px]
                    font-black
                    uppercase
                    text-slate-500
                    px-2
                    tracking-wider
                    whitespace-nowrap
                  "
                >
                  Page {pageNumber} of{" "}
                  {totalPages}
                </div>

                <button
                  type="button"
                  disabled={
                    pageNumber >= totalPages
                  }
                  onClick={() =>
                    setPageNumber(
                      (prev) =>
                        Math.min(
                          prev + 1,
                          totalPages
                        )
                    )
                  }
                  className="
                    w-7
                    h-7

                    flex
                    items-center
                    justify-center

                    border
                    rounded-lg

                    bg-white
                    hover:bg-slate-50

                    disabled:bg-slate-100

                    text-slate-600
                    disabled:text-slate-300

                    transition
                  "
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductManagement;